import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Video, Clock, X, Bell, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const SessionReminder = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [showReminder, setShowReminder] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [currentReminder, setCurrentReminder] = useState(null);

  // Check notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Browser does not support notifications');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notifications enabled! You will be reminded before sessions.');
      } else {
        toast.error('Notification permission denied');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  // Fetch upcoming sessions
  const checkUpcomingSessions = useCallback(async () => {
    if (!user || !api) return;

    try {
      const response = await api.get('/tutoring/sessions/upcoming-reminders');
      const sessions = response.data.upcoming_sessions || [];
      setUpcomingSessions(sessions);

      // Show reminder for sessions starting within 15 minutes
      for (const session of sessions) {
        if (session.minutes_until_start <= 15) {
          // Check if reminder already sent
          try {
            const statusRes = await api.get(`/tutoring/sessions/${session.id}/reminder-status`);
            if (!statusRes.data.reminder_sent) {
              showSessionReminder(session);
              // Mark reminder as sent
              await api.post(`/tutoring/sessions/${session.id}/reminder-sent`);
            }
          } catch (err) {
            // If status check fails, still show reminder
            showSessionReminder(session);
          }
        }
      }
    } catch (error) {
      console.error('Error checking upcoming sessions:', error);
    }
  }, [user, api]);

  // Show reminder notification
  const showSessionReminder = (session) => {
    setCurrentReminder(session);
    setShowReminder(true);

    // Also show browser notification if enabled
    if (notificationsEnabled && 'Notification' in window) {
      const notification = new Notification('Tutoring Session Starting Soon!', {
        body: `"${session.title}" starts in ${session.minutes_until_start} minutes`,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: `session-${session.id}`,
        requireInteraction: true
      });

      notification.onclick = () => {
        window.focus();
        navigate(`/tutoring/room/${session.id}`);
        notification.close();
      };
    }
  };

  // Poll for upcoming sessions every 5 minutes
  useEffect(() => {
    if (!user) return;

    // Initial check
    checkUpcomingSessions();

    // Set up polling interval (every 5 minutes)
    const interval = setInterval(checkUpcomingSessions, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, checkUpcomingSessions]);

  // Join session handler
  const handleJoinSession = () => {
    if (currentReminder) {
      navigate(`/tutoring/room/${currentReminder.id}`);
      setShowReminder(false);
    }
  };

  // Dismiss reminder
  const dismissReminder = () => {
    setShowReminder(false);
    setCurrentReminder(null);
  };

  // Don't render anything if user is not logged in
  if (!user) return null;

  // Only show for students and tutors
  if (!['student', 'tutor', 'admin'].includes(user.role)) return null;

  return (
    <>
      {/* Floating Reminder Popup */}
      {showReminder && currentReminder && (
        <div className="fixed bottom-4 right-4 z-50 w-80 animate-in slide-in-from-bottom-5">
          <div className="bg-white rounded-xl shadow-2xl border-2 border-emerald-500 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <BellRing className="w-5 h-5 animate-pulse" />
                <span className="font-bold">Session Starting Soon!</span>
              </div>
              <button
                onClick={dismissReminder}
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Video className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">{currentReminder.title}</h3>
                  <p className="text-sm text-slate-500">with {currentReminder.tutor_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-orange-600 bg-orange-50 rounded-lg px-3 py-2 mb-4">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-semibold">
                  Starts in {currentReminder.minutes_until_start} minute{currentReminder.minutes_until_start !== 1 ? 's' : ''}!
                </span>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleJoinSession}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="join-session-reminder-btn"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Join Now
                </Button>
                <Button
                  variant="outline"
                  onClick={dismissReminder}
                >
                  Later
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Permission Banner (only show once) */}
      {!notificationsEnabled && 'Notification' in window && Notification.permission === 'default' && (
        <div className="fixed bottom-4 left-4 z-40 max-w-sm">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-slate-800 mb-1">Enable Notifications</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Get reminded before your tutoring sessions start!
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={requestNotificationPermission}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="enable-notifications-btn"
                  >
                    Enable
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setNotificationsEnabled(true)}
                  >
                    Not now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionReminder;
